/**
 * ExternalConsultantsReport — /#/reports/external-consultants
 *
 * Admin-only. Lists all users with external_consultant = true plus their
 * tenant and role. Gating is enforced at the session level — non-admin
 * users see an Access Denied state.
 */
import { useEffect, useMemo, useState } from "react";
import ReportShell, { thStyle, tdStyle, BLACK, SLATE, BORDER, EmptyState, LoadingState } from "./_ReportShell";
import { ExternalConsultantRow, downloadCsv, listExternalConsultants, toCsv } from "@/lib/reports-saturday";
import { getSession } from "@/lib/session";

function isAdmin(): boolean {
  const s = getSession();
  if (!s) return false;
  const role = (s as any).role ?? "";
  return role === "manager" || role === "executive" || role === "admin";
}

export default function ExternalConsultantsReport() {
  const allowed = isAdmin();
  const [rows, setRows] = useState<ExternalConsultantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await listExternalConsultants();
        if (!cancelled) setRows(r);
      } catch (err: any) {
        console.error("[ExternalConsultantsReport]", err);
        if (!cancelled) setError(err?.message ?? "Failed to load");
      }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  const csv = useMemo(() => {
    if (!rows) return "";
    return toCsv(rows.map((r) => ({
      full_name: r.full_name ?? "",
      email: r.email,
      role: r.role,
      tenant_id: r.tenant_id,
      tenant_name: r.tenant_name ?? "",
    })));
  }, [rows]);

  if (!allowed) {
    return (
      <ReportShell title="External Consultants" subtitle="Admin only." rightLabel="External Consultants">
        <EmptyState message="Access denied. This report is admin-only." />
      </ReportShell>
    );
  }

  return (
    <ReportShell
      title="External Consultants"
      subtitle="Users flagged as external_consultant (RULE-EXT-CONSULTANT) with tenant and role."
      rightLabel="External Consultants"
      onExport={rows && rows.length > 0 ? () => downloadCsv("external-consultants.csv", csv) : undefined}
    >
      {error ? (
        <EmptyState message={`Could not load: ${error}`} />
      ) : !rows ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState message="No users flagged external_consultant." />
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, background: "#FFFFFF", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Tenant</th>
                <th style={thStyle}>Role</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_id}>
                  <td style={tdStyle}>{r.full_name ?? "—"}</td>
                  <td style={tdStyle}>{r.email}</td>
                  <td style={tdStyle}>{r.tenant_name ?? `tenant ${r.tenant_id}`}</td>
                  <td style={tdStyle}>{r.role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
}
